import { useEffect, useState, useCallback, memo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import "react-toastify/dist/ReactToastify.css";
import PageBanner from "./PageBanner";
import SocialMediaSidebar from "./SocialMediaSidebar";
import SectionHeader from "./SectionHeader";
import { EcomAPI, CMSAPI } from "../config";
import { FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ProgressBar from "./ProgressBar";
import ScrollToTopButton from "./ScrollToTopButton";

// Memoized Cart Item Component
const CartItem = memo(({ item, updateCartQuantity, deleteCartItem }) => {
  const price = parseFloat(item.price) || 0;
  const itemTotal = price * item.quantity;
  const imageUrl = item.mediaPath
    ? `${CMSAPI}${item.mediaPath}`
    : "/images/placeholder.png";

  // Debounce quantity updates
  const handleQuantityChange = useCallback(
    (isIncrement) => {
      updateCartQuantity(item.productId, isIncrement);
    },
    [item.productId, updateCartQuantity]
  );

  return (
    <div className="card mb-6 border rounded-lg overflow-hidden shadow-sm">
      <div className="flex flex-col md:flex-row">
        <div className="md:w-1/3 p-4">
          <img
            src={imageUrl}
            onError={(e) => (e.target.src = "/images/placeholder.png")}
            className="w-full h-auto object-cover rounded"
            alt={item.name}
            loading="lazy"
          />
        </div>
        <div className="md:w-2/3 p-4 relative">
          <button
            className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full"
            onClick={() => deleteCartItem(item.productId)}
          >
            <FaTrash className="w-4 h-4" />
          </button>
          <h5 className="text-lg font-semibold mb-2">{item.name}</h5>
          <p className="text-gray-700 mb-2">Price: ₹{price}</p>
          <div className="flex items-center mb-3">
            <button
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-1 px-3 rounded-l"
              onClick={() => handleQuantityChange(false)}
            >
              -
            </button>
            <span className="px-4 bg-gray-100 py-1">{item.quantity}</span>
            <button
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-1 px-3 rounded-r"
              onClick={() => handleQuantityChange(true)}
            >
              +
            </button>
          </div>
          <p className="text-sm text-gray-500">
            Total: ₹{itemTotal.toFixed(2)}
          </p>
        </div>
      </div>
    </div>
  );
});

const Cart = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Get cookie helper function
  const getCookie = useCallback((name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
  }, []);

  // Fetch cart data
  const fetchCartData = useCallback(async () => {
    setIsLoading(true);
    const userId = getCookie("userId");

    if (!userId) {
      // Handle guest cart
      const guestCart = JSON.parse(getCookie("guestCart") || "[]");
      if (guestCart.length === 0) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${CMSAPI}/api/Website/get-products?domain=swm`
        );
        const products = response.data;

        // const guestItems = guestCart
        //   .map((id) => products.find((p) => String(p.id) === String(id)))
        //   .filter(Boolean)
        //   .map((item) => ({ ...item, quantity: 1 }));

        const guestItems = guestCart
          .map((id) => {
            const product = products.find((p) => String(p.id) === String(id));
            return product
              ? {
                  ...product,
                  mediaPath: product.imagePath, // Normalize to mediaPath
                  quantity: 1,
                }
              : null;
          })
          .filter(Boolean);

        setCartItems(guestItems);
        calculateTotals(guestItems);
      } catch (err) {
        console.error("Failed to load guest product data:", err);
        toast.error("Failed to load cart items");
      }
      setIsLoading(false);
      return;
    }

    // Handle logged-in user cart
    try {
      const response = await axios.get(
        `${CMSAPI}/api/Website/get-cart-by-user-id?userId=${userId}`
      );
      const data = response.data;

      if (data.length > 0 && data[0].cartId) {
        const cartId = data[0].cartId;
        document.cookie = `cartId=${cartId}; path=/; max-age=86400`;
      }

      setCartItems(data);
      calculateTotals(data);

      // Update cart count badge
      const totalCount = data.reduce(
        (sum, item) => sum + (item.quantity || 0),
        0
      );
      const cartBadge = document.getElementById("cartCount");
      if (cartBadge) cartBadge.innerText = totalCount;
    } catch (error) {
      console.error("Failed to fetch cart data:", error);
      toast.error("Failed to fetch cart data");
    }
    setIsLoading(false);
  }, [getCookie]);

  // Calculate totals without re-rendering
  const calculateTotals = useCallback((items) => {
    const validItems = items.filter(
      (item) => item && item.productId && item.name && item.quantity != null
    );

    if (validItems.length === 0) {
      setTotalAmount(0);
      setTotalItems(0);
      return;
    }

    const { total, count } = validItems.reduce(
      (acc, item) => {
        const price = parseFloat(item.price) || 0;
        const quantity = item.quantity;
        acc.total += price * quantity;
        acc.count += quantity;
        return acc;
      },
      { total: 0, count: 0 }
    );

    setTotalAmount(total);
    setTotalItems(count);
  }, []);

  // Optimistic update for quantity changes
  const updateCartQuantity = useCallback(
    async (productId, isIncrement) => {
      // Optimistic UI update
      setCartItems((prevItems) => {
        return prevItems.map((item) => {
          if (String(item.productId) === String(productId)) {
            const newQuantity = isIncrement
              ? item.quantity + 1
              : Math.max(1, item.quantity - 1);
            return { ...item, quantity: newQuantity };
          }
          return item;
        });
      });

      const userId = getCookie("userId");
      const cartId = getCookie("cartId");

      // Guest user logic
      if (!userId) {
        try {
          let guestCart = JSON.parse(getCookie("guestCart") || "[]");
          const itemCount = guestCart.filter((id) => id === productId).length;

          if (isIncrement) {
            guestCart.push(productId);
          } else {
            if (itemCount <= 1) {
              toast.warning("Quantity cannot be less than 1");
              return;
            }
            const indexToRemove = guestCart.lastIndexOf(productId);
            if (indexToRemove !== -1) guestCart.splice(indexToRemove, 1);
          }

          document.cookie = `guestCart=${JSON.stringify(
            guestCart
          )}; path=/; max-age=86400`;
          toast.success("Updated Successfully!");
          return;
        } catch (error) {
          toast.error("Failed to update cart");
          console.error("Guest cart update failed:", error);
          fetchCartData(); // Rollback if failed
        }
        return;
      }

      // Logged-in user logic
      if (!isIncrement) {
        try {
          const response = await axios.get(
            `${CMSAPI}/api/Website/get-cart-by-user-id?userId=${userId}`
          );
          const cartData = response.data;
          const currentItem = cartData.find(
            (item) => String(item.productId) === String(productId)
          );

          if (!currentItem || currentItem.quantity <= 1) {
            toast.warning("Quantity cannot be less than 1");
            return;
          }
        } catch (err) {
          console.error("Failed to check quantity:", err);
          toast.error("Failed to update quantity");
          fetchCartData(); // Rollback if failed
          return;
        }
      }

      const payload = {
        id: parseInt(cartId),
        productId: productId,
        userId: parseInt(userId),
        isSingle: true,
      };

      try {
        if (isIncrement) {
          await axios.post(`${CMSAPI}/api/Website/add-to-cart`, payload);
        } else {
          await axios.delete(`${CMSAPI}/api/Website/delete-from-cart`, {
            data: payload,
          });
        }
        toast.success("Updated Successfully!");
      } catch (error) {
        console.error("Cart update failed:", error);
        toast.error("Failed to update item");
        fetchCartData(); // Rollback if failed
      }
    },
    [fetchCartData, getCookie]
  );

  // Delete cart item with optimistic update
  const deleteCartItem = useCallback(
    async (productId) => {
      // Optimistic UI update
      setCartItems((prevItems) =>
        prevItems.filter((item) => String(item.productId) !== String(productId))
      );

      const userId = getCookie("userId");

      // Guest user logic
      if (!userId) {
        try {
          let guestCart = JSON.parse(getCookie("guestCart") || "[]");
          guestCart = guestCart.filter((id) => id !== productId);
          document.cookie = `guestCart=${JSON.stringify(
            guestCart
          )}; path=/; max-age=86400`;
          toast.success("Item removed successfully");
          return;
        } catch (error) {
          toast.error("Failed to remove item");
          console.error("Guest cart item removal failed:", error);
          fetchCartData(); // Rollback if failed
        }
        return;
      }

      // Logged-in user logic
      const cartId = getCookie("cartId");

      if (!cartId) {
        toast.error("Missing cart ID. Please refresh or log in again.");
        fetchCartData(); // Rollback
        return;
      }

      try {
        const response = await axios.delete(
          `${CMSAPI}/api/Website/delete-from-cart`,
          {
            data: {
              id: parseInt(cartId),
              productId: parseInt(productId),
              // Include userId if required by your API
              ...(userId && { userId: parseInt(userId) }),
            },
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        // Handle different response formats
        const result = response.data;
        if (result === "OK" || result?.status === true) {
          toast.success("Item deleted successfully!");
        } else {
          throw new Error("Failed to delete item from cart");
        }
      } catch (error) {
        console.error("Error deleting cart item:", error);
        toast.error(error.response?.data?.message || "Failed to remove item");
        fetchCartData(); // Rollback if failed
      }
    },
    [fetchCartData, getCookie]
  );

  const handleProceed = useCallback(() => {
    const isLoggedIn = getCookie("isLoggedIn");
    const userName = getCookie("userName");

    if (isLoggedIn === "true" && userName) {
      navigate("/checkout");
    } else {
      navigate("/signin");
    }
  }, [getCookie]);

  useEffect(() => {
    fetchCartData();
  }, [fetchCartData]);

  // Update totals when cartItems change
  useEffect(() => {
    calculateTotals(cartItems);
  }, [cartItems, calculateTotals]);

  return (
    <>
      <Toaster
        position="top-center"
        containerStyle={{
          top: "4rem", // Adjust based on navbar height
          zIndex: 100000,
        }}
        toastOptions={{
          style: {
            zIndex: 100000,
          },
        }}
      />
      <div>
        <SocialMediaSidebar />
      </div>
      <div>
        <ScrollToTopButton />
      </div>
      <PageBanner
        title="My Cart"
        breadcrumb={[{ label: "Home", href: "/" }, { label: "My Cart" }]}
      />
      <div>
      <ProgressBar cartItemsCount={cartItems.length}/>
      {/* Your shop content */}
    </div>

      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-28 py-10">
        <div className="cart-container">
          {isLoading ? (
            <div className="text-center mt-4">Loading cart...</div>
          ) : cartItems.length === 0 ? (
            <div className="text-center mt-4">
              <button
                className="bg-[#00D084] hover:bg-[#00D084] text-white font-bold py-2 px-4 rounded"
                onClick={() => navigate("/shop")}
              >
                Your Cart is Empty - Start Shopping Now
              </button>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-2/3">
                {cartItems.map((item) => (
                  <CartItem
                    key={`${item.productId}-${item.quantity}`} // Unique key based on quantity
                    item={item}
                    updateCartQuantity={updateCartQuantity}
                    deleteCartItem={deleteCartItem}
                  />
                ))}
              </div>

              <div className="lg:w-1/3">
                <div className="border p-4 rounded-lg shadow-sm">
                  <div className="font-bold border-b pb-2 mb-3">
                    PRICE DETAILS - TRIAL
                  </div>
                  <div className="flex justify-between mb-2">
                    <span>Total MRP</span>
                    <span>₹{totalAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span>Discount on MRP</span>
                    <span className="text-green-600">-₹0</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span>Delivery Charges</span>
                    <span className="text-green-600">FREE</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span>Total Items</span>
                    <span>{totalItems}</span>
                  </div>
                  <div className="flex justify-between font-bold border-t pt-2 mb-3">
                    <span>Total Amount</span>
                    <span>₹{totalAmount.toFixed(2)}</span>
                  </div>
                  <button
                    className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded w-full"
                    onClick={handleProceed}
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Cart;
